"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  numeral: "01",
  name: "Automotive Acquisition",
  elevation: "STRATEGIC · MARKET ANALYSIS",
  description: "We leverage real-time market data to ensure you pay fair value for your next vehicle, eliminating dealer markups and hidden fees.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723817/img/auto_negotiation.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723817/img/auto_negotiation.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723817/img/auto_negotiation.png-wvc-sizes"
}, {
  numeral: "02",
  name: "Contractor & Home Improvement Quote Review",
  elevation: "TECHNICAL · COST VALIDATION",
  description: "We analyze contractor bids and renovation quotes to ensure material costs and labor hours align with industry standards, preventing overpayment on home projects.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723817/img/contractor_review.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723817/img/contractor_review.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723817/img/contractor_review.png-wvc-sizes"
}, {
  numeral: "04",
  name: "Contract Review & Legal Alignment",
  elevation: "EXPERT · LEGAL ALIGNMENT",
  description: "Detailed analysis of purchase agreements to identify unfavorable clauses and ensure your rights are fully protected.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723817/img/contract_review.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723817/img/contract_review.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723817/img/contract_review.png-wvc-sizes"
}];
export default function Negotiation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="3116" id="our-negotiation-expertise" className="bg-background text-foreground relative overflow-hidden py-24 md:py-36 lg:py-[200px]">
      <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-40">
        {Array.from({
        length: 24
      }).map((_, i) => <span key={i} data-index={i} className="absolute block rounded-lg bg-primary/30" style={{
        width: `${1 + i % 3}px`,
        height: `${1 + i % 3}px`,
        left: `${i * 53 % 100}%`,
        top: `${i * 37 % 100}%`,
        animation: `shelter-drift ${18 + i % 7 * 2}s linear ${i * 0.5}s infinite`
      }} />)}
      </div>

      <style>{`
        @keyframes shelter-drift {
          0% { transform: translate3d(0, 0, 0); opacity: 0; }
          15% { opacity: 0.6; }
          85% { opacity: 0.6; }
          100% { transform: translate3d(40px, -120px, 0); opacity: 0; }
        }
      `}</style>

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        <div ref={headerRef} className={`relative mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="relative flex flex-col md:block">
            <Link to="/" className="block select-none md:absolute md:-top-12 md:-left-8 z-0 opacity-10 hover:opacity-20 transition-opacity duration-500">
              <img src="/img/logo-large.png" alt="Price Advocate Services" className="h-40 sm:h-56 md:h-72 lg:h-80 w-auto object-contain" loading="lazy" decoding="async" src="https://priceadvocates.10web.cloud/wp-content/uploads/2026/07/1D69EE56-30DB-4891-9EEC-CB9865D2838D-2.png" />
            </Link>

            <div className="relative md:pl-[40%] md:pt-16 lg:pt-24 z-10">
              <p className="mb-6 text-xs tracking-[0.25em] uppercase text-[hsl(25_82%_53%)] font-default font-bold">
                Professional Advocacy · Proven Results
              </p>
              <h2 className="font-default font-bold leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-[88px] text-[hsl(217_61%_26%)]">Big Purchase Coming Up? <em>Bring Backup.</em></h2>
              <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[hsl(0_0%_20%)]">Price Advocate Services helps you review, understand, and negotiate major purchases before you sign — so you can move forward with less pressure and more confidence</p>
            </div>
          </div>
        </div>

        <div className="relative">
          <Separator className="bg-[hsl(210_20%_90%)]" />
          {services.map((service, i) => {
          return <ServiceRow key={service.name} data-index={i} {...service} />;
        })}
        </div>

        <div ref={ctaRef} className={`mt-20 md:mt-28 flex flex-col items-center text-center transition-all duration-1000 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="mb-6 max-w-lg text-sm md:text-base text-[hsl(0_0%_45%)] leading-relaxed">
            Ready to secure a better deal? Our consultation process is simple, transparent, and designed to save you time and money.
          </p>
          <Link to="/PricingPage">
            <Button size="lg" className="h-14 px-8 rounded-lg bg-[hsl(25_82%_53%)] text-white hover:bg-[hsl(25_82%_45%)] font-default text-sm tracking-[0.15em] uppercase shadow-md">
              Start Your Consultation
              <ArrowUpRight className="ml-1 size-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}
type ServiceRowProps = {
  numeral: string;
  name: string;
  elevation: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  "data-index"?: number;
};
function ServiceRow({
  numeral,
  name,
  elevation,
  description,
  image,
  srcset,
  sizes,
  ...rest
}: ServiceRowProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div {...rest}>
      <div ref={ref} className={`group grid grid-cols-12 gap-4 md:gap-8 items-center py-8 md:py-10 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="col-span-2 md:col-span-1">
          <span className="font-default text-base md:text-lg tracking-[0.2em] text-[hsl(25_82%_53%)] font-bold">
            {numeral}
          </span>
        </div>

        <div className="col-span-10 md:col-span-7 lg:col-span-8">
          <h3 className="font-default font-bold leading-tight text-3xl sm:text-4xl md:text-[44px] text-[hsl(217_61%_26%)]">
            {name}
          </h3>
          <p className="mt-2 text-[11px] tracking-[0.2em] uppercase text-[hsl(217_52%_37%)] font-default font-bold">
            {elevation}
          </p>
          <p className="mt-4 max-w-xl text-sm md:text-base leading-relaxed text-[hsl(0_0%_20%)]">
            {description}
          </p>
          <Link to="/ServicesPage" className="mt-5 inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-[hsl(25_82%_53%)] hover:text-[hsl(217_61%_26%)] transition-colors duration-300 opacity-0 group-hover:opacity-100 md:block md:opacity-100 md:mt-5 lg:hidden">
            Learn More
            <ArrowUpRight className="size-3.5" />
          </Link>
        </div>

        <div className="hidden lg:flex col-span-3 items-center justify-end gap-6">
          <Link to="/ServicesPage" className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-[hsl(25_82%_53%)] opacity-0 -translate-x-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0 whitespace-nowrap">
            Learn More
            <ArrowUpRight className="size-3.5" />
          </Link>
          <div className="w-[200px] xl:w-[240px] overflow-hidden rounded-lg shadow-md">
            <AspectRatio ratio={3 / 4}>
              <img src={image} data-wvc-srcset={srcset} data-wvc-sizes={sizes} alt={`Professional representation for ${name}`} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
            </AspectRatio>
          </div>
        </div>

        <div className="col-span-12 md:col-span-4 lg:hidden">
          <div className="overflow-hidden rounded-lg shadow-md ml-auto w-full md:max-w-[220px]">
            <AspectRatio ratio={3 / 4}>
              <img src={image} data-wvc-srcset={srcset} data-wvc-sizes={sizes} alt={`Professional representation for ${name}`} loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </AspectRatio>
          </div>
        </div>
      </div>
      <Separator className="bg-[hsl(210_20%_90%)]" />
    </div>;
}